/**
 * useWorkspacePersistence — server-backed workspace state management.
 *
 * Replaces all sessionStorage with API-backed persistence.
 * On mount: fetches workspace list from backend.
 * On switch: hydrates full workspace state (conversations, artifacts, clinical data).
 * On UI state changes: debounced 2s save to backend.
 */

import { useState, useCallback, useRef, useEffect } from 'react'
import {
  workspaceService,
  type WorkspaceSummary,
  type WorkspaceType,
  type WorkspaceUpdate,
  type HydrationResponse,
} from '../services/workspaceService'

// ────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────

interface UseWorkspacePersistenceReturn {
  /** Workspace summaries for sidebar listing */
  workspaces: WorkspaceSummary[]
  /** Currently active workspace ID */
  activeWorkspaceId: string | null
  /** True while hydration request is in flight */
  isHydrating: boolean
  /** True while initial workspace list is loading */
  isLoading: boolean
  /** Error from last operation, if any */
  error: string | null
  /** Latest hydration payload (null until first hydration) */
  hydrationData: HydrationResponse | null

  /** Create a new workspace, returns its ID */
  createWorkspace: (type: WorkspaceType, name?: string) => Promise<string>
  /** Switch to a workspace and hydrate its state */
  switchWorkspace: (id: string) => Promise<void>
  /** Close (soft) or delete (hard) a workspace */
  closeWorkspace: (id: string, hard?: boolean) => Promise<void>
  /** Reopen a previously closed workspace */
  reopenWorkspace: (id: string) => Promise<void>
  /** Debounced save of workspace state updates (2s) */
  saveState: (updates: Partial<WorkspaceUpdate>) => void
  /** Refresh the workspace list from the server */
  refreshList: (includeClosed?: boolean) => Promise<void>
  /** Re-hydrate a workspace (re-fetch artifacts, conversations, clinical data) */
  rehydrate: (id: string) => Promise<void>
  /** Refresh just clinical data for a workspace (merges into existing hydrationData) */
  refreshClinicalData: (id: string) => Promise<void>
  /** Refresh a single artifact by ID (merges into existing hydrationData.artifacts) */
  refreshArtifact: (id: string, artifactId: string) => Promise<void>
  /** Immediately flush any pending debounced saves */
  flushSave: () => Promise<void>
  /** Save and deselect current workspace (no close, no hydrate) */
  deselectWorkspace: () => Promise<void>
}

// ────────────────────────────────────────────────────────
// Debounce helper
// ────────────────────────────────────────────────────────

const DEBOUNCE_MS = 2000

const getInitialWorkspaceIdFromPath = (): string | null => {
  if (typeof window === 'undefined') return null
  const match = window.location.pathname.match(/^\/workspace\/([^/?#]+)/)
  return match ? match[1] : null
}

// ────────────────────────────────────────────────────────
// Hook
// ────────────────────────────────────────────────────────

export const useWorkspacePersistence = (): UseWorkspacePersistenceReturn => {
  const [workspaces, setWorkspaces] = useState<WorkspaceSummary[]>([])
  const [activeWorkspaceId, setActiveWorkspaceId] = useState<string | null>(null)
  // Start true when we're deep-linked into a specific workspace — otherwise
  // render-1 of a cold deep-link briefly shows the default IntakePage (no
  // existing files, button disabled) before the hydrate effect commits.
  const [isHydrating, setIsHydrating] = useState(() => getInitialWorkspaceIdFromPath() !== null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [hydrationData, setHydrationData] = useState<HydrationResponse | null>(null)

  // Debounce timer ref for saveState
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Accumulated updates to batch into a single save
  const pendingUpdatesRef = useRef<Partial<WorkspaceUpdate>>({})
  // Track mounted state to avoid state updates after unmount
  const mountedRef = useRef(true)
  const initialWorkspaceIdRef = useRef<string | null>(getInitialWorkspaceIdFromPath())
  // Ref to avoid stale closure in saveState timer (Bug 20: save could target wrong workspace)
  const activeWorkspaceIdRef = useRef(activeWorkspaceId)
  activeWorkspaceIdRef.current = activeWorkspaceId

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      // Flush pending save on unmount (use ref to get current workspace ID)
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current)
        saveTimerRef.current = null
        const id = activeWorkspaceIdRef.current
        const updates = pendingUpdatesRef.current
        if (id && Object.keys(updates).length > 0) {
          workspaceService.update(id, updates).catch((err) => {
            console.warn('[useWorkspacePersistence] Failed to save workspace on unmount:', err)
          })
          pendingUpdatesRef.current = {}
        }
      }
    }
  }, [])

  // ── Fetch workspace list on mount ──

  const refreshList = useCallback(async (includeClosed = false) => {
    try {
      setIsLoading(true)
      setError(null)
      const list = await workspaceService.list(includeClosed)
      if (mountedRef.current) {
        setWorkspaces(list)
      }
    } catch (err) {
      if (mountedRef.current) {
        const message = err instanceof Error ? err.message : 'Failed to load workspaces'
        setError(message)
        console.error('[WorkspacePersistence] Failed to list workspaces:', err)
      }
    } finally {
      if (mountedRef.current) {
        setIsLoading(false)
      }
    }
  }, [])


  // ── Hydrate a workspace ──

  const hydrationCounterRef = useRef(0)

  const hydrateWorkspace = useCallback(async (id: string) => {
    const requestId = ++hydrationCounterRef.current
    try {
      setIsHydrating(true)
      setError(null)
      const data = await workspaceService.hydrate(id)
      // Only apply if this is still the most recent hydration request
      if (mountedRef.current && requestId === hydrationCounterRef.current) {
        setHydrationData(data)
        setActiveWorkspaceId(id)
      }
    } catch (err) {
      if (mountedRef.current && requestId === hydrationCounterRef.current) {
        const message = err instanceof Error ? err.message : 'Failed to hydrate workspace'
        setError(message)
        console.error('[WorkspacePersistence] Failed to hydrate workspace:', err)
      }
      throw err
    } finally {
      if (mountedRef.current && requestId === hydrationCounterRef.current) {
        setIsHydrating(false)
      }
    }
  }, [])

  // ── Create workspace ──

  useEffect(() => {
    refreshList()
    const initialId = initialWorkspaceIdRef.current
    initialWorkspaceIdRef.current = null
    if (initialId) {
      // hydrateWorkspace already calls setError on failure; rethrow goes nowhere,
      // so log here for forensics. A 404 deep-link leaves the user on a blank
      // workspace list — UX recovery happens via the `error` state.
      void hydrateWorkspace(initialId).catch((err) =>
        console.warn('[WorkspacePersistence] Deep-link hydration failed:', err)
      )
    }
  }, [refreshList, hydrateWorkspace])

  const createWorkspace = useCallback(async (type: WorkspaceType, name?: string): Promise<string> => {
    try {
      setError(null)
      const created = await workspaceService.create(type, name)
      if (mountedRef.current) {
        // Add to local list immediately (optimistic)
        const summary: WorkspaceSummary = {
          id: created.id,
          type: created.type,
          display_name: created.display_name,
          active_phase: created.active_phase,
          is_closed: false,
          updated_at: created.created_at,
        }
        setWorkspaces(prev => [...prev, summary])
        // Hydrate the new workspace
        await hydrateWorkspace(created.id)
      }
      return created.id
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create workspace'
      if (mountedRef.current) {
        setError(message)
      }
      console.error('[WorkspacePersistence] Failed to create workspace:', err)
      throw err
    }
  }, [hydrateWorkspace])

  // ── Flush pending save ──

  const flushSave = useCallback(async () => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current)
      saveTimerRef.current = null
    }
    const id = activeWorkspaceIdRef.current
    const updates = pendingUpdatesRef.current
    if (id && Object.keys(updates).length > 0) {
      pendingUpdatesRef.current = {}
      try {
        await workspaceService.update(id, updates)
      } catch (err) {
        console.error('[WorkspacePersistence] Failed to flush save:', err)
        pendingUpdatesRef.current = { ...updates, ...pendingUpdatesRef.current }
      }
    }
  }, []) // activeWorkspaceId accessed via ref to avoid stale closure

  // ── Switch workspace ──

  const switchWorkspace = useCallback(async (id: string) => {
    if (id === activeWorkspaceId) return
    await flushSave()
    await hydrateWorkspace(id)
  }, [activeWorkspaceId, flushSave, hydrateWorkspace])

  // ── Deselect workspace ──

  const deselectWorkspace = useCallback(async () => {
    await flushSave()
    setActiveWorkspaceId(null)
    setHydrationData(null)
  }, [flushSave])

  // ── Close workspace ──

  const closeWorkspace = useCallback(async (id: string, hard = false) => {
    try {
      setError(null)
      await workspaceService.close(id, hard)
      if (mountedRef.current) {
        if (id === activeWorkspaceId) {
          // Closing the active workspace — remove and switch to next
          setWorkspaces(prev => {
            const remaining = prev.filter(ws => ws.id !== id)
            if (remaining.length > 0) {
              hydrateWorkspace(remaining[0].id).catch((err) => { console.error('[useWorkspacePersistence] Failed to hydrate fallback workspace:', err) })
            } else {
              setActiveWorkspaceId(null)
              setHydrationData(null)
            }
            return remaining
          })
        } else {
          // Closing a non-active workspace — just remove
          setWorkspaces(prev => prev.filter(ws => ws.id !== id))
        }
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to close workspace'
      if (mountedRef.current) {
        setError(message)
      }
      console.error('[WorkspacePersistence] Failed to close workspace:', err)
      throw err
    }
  }, [activeWorkspaceId, hydrateWorkspace])

  // ── Reopen workspace ──

  const reopenWorkspace = useCallback(async (id: string) => {
    try {
      setError(null)
      const reopened = await workspaceService.reopen(id)
      if (mountedRef.current) {
        const summary: WorkspaceSummary = {
          id: reopened.id,
          type: reopened.type,
          display_name: reopened.display_name,
          active_phase: reopened.active_phase,
          is_closed: false,
          updated_at: reopened.updated_at,
        }
        setWorkspaces(prev => [...prev, summary])
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to reopen workspace'
      if (mountedRef.current) {
        setError(message)
      }
      console.error('[WorkspacePersistence] Failed to reopen workspace:', err)
      throw err
    }
  }, [])

  // ── Granular refresh (merge into existing hydrationData) ──

  const refreshClinicalData = useCallback(async (id: string) => {
    if (id !== activeWorkspaceIdRef.current) return
    try {
      const clinicalData = await workspaceService.fetchClinicalData(id)
      if (mountedRef.current && id === activeWorkspaceIdRef.current) {
        setHydrationData(prev => prev ? { ...prev, clinical_data: clinicalData } : prev)
      }
    } catch (err) {
      // Log AND rethrow. Swallowing here made every caller unable to tell a
      // refresh that happened from one that failed: ChatContext's manual-save
      // path wraps this in try/catch to warn the clinician that the panel still
      // shows pre-edit values, and that warning could never fire — a clinical
      // data GET that 500s or times out resolved cleanly, so the dialog closed
      // on an unambiguous "saved" over a stale panel. The two SSE-completion
      // callers already attach .catch(), so they only gain the error they were
      // written to log.
      console.error('[WorkspacePersistence] Failed to refresh clinical data:', err)
      throw err
    }
  }, [])

  const refreshArtifact = useCallback(async (id: string, artifactId: string) => {
    if (id !== activeWorkspaceIdRef.current) return
    try {
      const artifact = await workspaceService.fetchArtifact(artifactId)
      if (mountedRef.current && id === activeWorkspaceIdRef.current) {
        setHydrationData(prev => {
          if (!prev) return prev
          const existing = prev.artifacts || []
          const idx = existing.findIndex(a => a.id === artifactId)
          const updated = idx >= 0
            ? existing.map(a => a.id === artifactId ? artifact : a)
            : [...existing, artifact]
          return { ...prev, artifacts: updated }
        })
      }
    } catch (err) {
      console.error('[WorkspacePersistence] Failed to refresh artifact:', err)
    }
  }, [])

  // ── Debounced state save ──

  const saveState = useCallback((updates: Partial<WorkspaceUpdate>) => {
    // Merge with any pending updates
    pendingUpdatesRef.current = { ...pendingUpdatesRef.current, ...updates }

    // Clear existing timer
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current)
    }

    // Schedule a save (use ref to get current workspace ID, not stale closure)
    saveTimerRef.current = setTimeout(async () => {
      const id = activeWorkspaceIdRef.current
      const pending = pendingUpdatesRef.current
      pendingUpdatesRef.current = {}
      saveTimerRef.current = null

      if (!id || Object.keys(pending).length === 0) return

      try {
        const updated = await workspaceService.update(id, pending)
        if (mountedRef.current) {
          // Update the summary in the local list
          setWorkspaces(prev =>
            prev.map(ws =>
              ws.id === id
                ? {
                    ...ws,
                    display_name: updated.display_name,
                    active_phase: updated.active_phase,
                    updated_at: updated.updated_at,
                  }
                : ws
            )
          )
        }
      } catch (err) {
        console.error('[WorkspacePersistence] Failed to save state:', err)
        // Re-queue the failed updates for next save attempt
        if (mountedRef.current) {
          pendingUpdatesRef.current = { ...pending, ...pendingUpdatesRef.current }
        }
      }
    }, DEBOUNCE_MS)
  }, []) // activeWorkspaceId accessed via ref to avoid stale closure in timer

  return {
    workspaces,
    activeWorkspaceId,
    isHydrating,
    isLoading,
    error,
    hydrationData,
    createWorkspace,
    switchWorkspace,
    closeWorkspace,
    reopenWorkspace,
    saveState,
    refreshList,
    rehydrate: hydrateWorkspace,
    refreshClinicalData,
    refreshArtifact,
    flushSave,
    deselectWorkspace,
  }
}
